/**
 * Service for retrieving feature flags from AWS
 */
class FeatureFlagsService {
  /**
   * Gets the feature flags URL based on the environment
   * @returns {string} - Feature flags URL
   */
  static getFeatureFlagUrl() {
    switch (process.env.REACT_APP_ENV) {
      case 'development':
        return 'https://cfg.dev.eroninternational.com/featureFlags.json';
      case 'staging':
        return 'https://cfg.stg.eroninternational.com/featureFlags.json';
      case 'production':
        return 'https://cfg.eroninternational.com/featureFlags.json';
      default:
        return 'https://cfg.dev.eroninternational.com/featureFlags.json';
    }
  }

  /**
   * Gets the global feature flags
   * @returns {Promise<Object>} - Global feature flags
   */
  static async fetchFeatureFlags() {
    try {
      // Get the current panel type
      const currentPanelType = process.env.REACT_APP_PANEL_TYPE || 'DIRECTA';
      
      // If not TUPAY panel, return an empty object to not affect other panels
      if (currentPanelType !== 'TUPAY') {
        return {};
      }
      
      // Only for TUPAY, get the feature flags from AWS
      const url = this.getFeatureFlagUrl();

      const response = await fetch(url);
      const data = await response.json();

      // Specifically verify the merchantFeatures structure
      if (data && data.merchantFeatures) {
        // Specifically verify requestSettlement
        if (data.merchantFeatures.requestSettlement) {
          // Feature exists
        } else {
          console.warn('[FeatureFlagsService] requestSettlement feature not found in merchantFeatures');
        }
      } else {
        console.warn('[FeatureFlagsService] merchantFeatures section not found in feature flags');
      }
      
      return data;
    } catch (error) {
      console.error('[FeatureFlagsService] Error fetching feature flags:', error);
      return {};
    }
  }

  /**
   * Gets the merchant-specific feature flags
   * @param {string} merchantId - Merchant ID
   * @returns {Promise<object>} - Object with feature flags
   * specific to the given merchant. In the current structure, merchant-specific
   * feature flags are included in the global configuration.
   */
  static async fetchMerchantFeatureFlags(merchantId) {
    try {
      // Validate if merchantId is null, undefined or empty
      if (merchantId === null || merchantId === undefined || merchantId === '') {
        console.warn('[FeatureFlagsService] Invalid merchantId provided:', merchantId);
        return {};
      }
      
      // Get the current panel type
      const currentPanelType = process.env.REACT_APP_PANEL_TYPE || 'DIRECTA';
      
      // MODIFICACIÓN: Si no es el panel TUPAY, devolver todos los features habilitados
      if (currentPanelType !== 'TUPAY') {
        // Para otros paneles, devolver un objeto con todas las features habilitadas
        return {
          requestSettlement_enabled: true
          // Añadir aquí otras features si es necesario
        };
      }
      
      // A partir de aquí, solo se ejecuta para el panel TUPAY
      // Get the global feature flags that already contain the merchant configuration
      const globalFlags = await this.fetchFeatureFlags();
      
      // If the merchantFeatures section exists, process the flags for this specific merchant
      if (globalFlags && globalFlags.merchantFeatures) {
        const merchantFeatures = globalFlags.merchantFeatures;
        const merchantSpecificFlags = {};
        
        // Iterate through all features and check if the merchant is in the whitelist or blacklist
        Object.keys(merchantFeatures).forEach(featureKey => {
          const feature = merchantFeatures[featureKey];
          
          // First check if the feature is globally enabled
          const isGloballyEnabled = feature.enabled !== false;
          
          // Convert merchantId to string to ensure correct comparison
          const merchantIdStr = String(merchantId);
          
          // Check for panel-specific merchant configuration for TUPAY
          if (feature.panelMerchants && feature.panelMerchants['TUPAY']) {
            const panelConfig = feature.panelMerchants['TUPAY'];
            
            // Check blacklist first (blacklist has priority)
            if (panelConfig.blacklist && panelConfig.blacklist.some(id => String(id) === merchantIdStr)) {
              merchantSpecificFlags[`${featureKey}_enabled`] = false;
            }
            // Check if there's a non-empty whitelist
            else if (panelConfig.whitelist && panelConfig.whitelist.length > 0) {
              const isInWhitelist = panelConfig.whitelist.some(id => String(id) === merchantIdStr);
              merchantSpecificFlags[`${featureKey}_enabled`] = isInWhitelist;
            }
            // If TUPAY panel has a configuration but no whitelist/blacklist restrictions,
            // all merchants in this panel get the feature
            else {
              merchantSpecificFlags[`${featureKey}_enabled`] = true;
            }
          }
          // Fall back to global merchant restrictions if no panel-specific config
          else if (feature.merchants) {
            // Check if the merchant is in the blacklist first (blacklist has priority)
            if (feature.merchants.blacklist && 
                feature.merchants.blacklist.some(id => String(id) === merchantIdStr)) {
              merchantSpecificFlags[`${featureKey}_enabled`] = false;
            }
            // Check if there's a non-empty whitelist
            else if (feature.merchants.whitelist && feature.merchants.whitelist.length > 0) {
              // If there's a whitelist, only merchants in the whitelist get the feature
              const isInWhitelist = feature.merchants.whitelist.some(id => String(id) === merchantIdStr);
              merchantSpecificFlags[`${featureKey}_enabled`] = isInWhitelist;
            } 
            // If whitelist is empty or not defined, all merchants get the feature
            else {
              // Use the global enabled flag
              if (isGloballyEnabled) {
                merchantSpecificFlags[`${featureKey}_enabled`] = true;
              }
            }
          }
          // No merchant restrictions at all
          else {
            if (isGloballyEnabled) {
              merchantSpecificFlags[`${featureKey}_enabled`] = true;
            }
          }
        });
        
        return merchantSpecificFlags;
      } else {
        console.warn('[FeatureFlagsService] No merchantFeatures section found in global flags');
      }
      
      return {};
    } catch (error) {
      console.error(`[FeatureFlagsService] Error fetching feature flags for merchant ${merchantId}:`, error);
      return {};
    }
  }
}

export default FeatureFlagsService;
